import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";

const LOGO = "/assets/logo.png";

const navLinks = [
  { label: "The Experience", href: "#experience" },
  { label: "The Entertainment", href: "#entertainment" },
  { label: "The Dining", href: "#dining" },
  { label: "The Destinations", href: "#destinations" },
  { label: "The Socials", href: "#socials" },
  { label: "The Gift", href: "#gift" },
  { label: "The PA", href: "#assistant" },
  { label: "The Official Launch", href: "#launch" },
  { label: "Partnership", href: "/partnership" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleNavClick = (e, href) => {
    setMenuOpen(false);
    // Anchor links (#) — only need rewriting if we're not on the home page.
    if (href.startsWith("#") && window.location.pathname !== "/") {
      e.preventDefault();
      window.location.href = "/" + href;
    }
  };

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 w-full z-40 transition-all duration-500 ${
          scrolled ? "bg-black/80 nav-glass border-b border-white/5 py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between">
          {/* Hamburger (desktop + mobile) */}
          <button
            className="text-white p-2 -ml-2 hover:opacity-70 transition-opacity"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            data-testid="menu-toggle-btn"
          >
            <Menu className="w-6 h-6" strokeWidth={1.5} />
          </button>

          {/* Logo center */}
          <a href="/" className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO} alt="AirStageX" className="h-14 sm:h-20" />
          </a>

          {/* Request a Quote (desktop) — label changed to Join Our Waitlist */}
          <a
            href="#access"
            onClick={(e) => handleNavClick(e, "#access")}
            className="btn-pill btn-pill-primary !hidden sm:!inline-flex"
            data-testid="nav-request-quote-btn"
          >
            Invitation Only
          </a>

          {/* Mobile compact CTA */}
          <a
            href="#access"
            onClick={(e) => handleNavClick(e, "#access")}
            className="sm:hidden text-white text-[10px] tracking-[0.15em] uppercase font-medium whitespace-nowrap"
            data-testid="nav-request-quote-mobile"
          >
            Invitation Only
          </a>
        </div>
      </nav>

      {/* Side drawer + backdrop */}
      <div
        className={`side-drawer-backdrop ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(false)}
        data-testid="drawer-backdrop"
      />
      <aside
        className={`side-drawer ${menuOpen ? "open" : ""}`}
        data-testid="side-drawer"
        aria-hidden={!menuOpen}
      >
        <div className="p-8 sm:p-12 flex items-start justify-between border-b border-white/5">
          <img src={LOGO} alt="AirStageX" className="h-10" />
          <button
            onClick={() => setMenuOpen(false)}
            className="text-white p-2 -mr-2 hover:opacity-70 transition-opacity"
            aria-label="Close menu"
            data-testid="drawer-close-btn"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>

        <nav className="p-8 sm:p-12">
          <p className="overline text-zinc-500 mb-6">Explore</p>
          <ul className="space-y-5">
            {navLinks.map((link, idx) => (
              <li key={link.href} style={{ transitionDelay: `${idx * 60}ms` }}>
                <a
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="group flex items-baseline gap-4 text-white text-2xl sm:text-3xl font-light tracking-wide hover:text-[#007AFF] transition-colors"
                  data-testid={`drawer-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="text-[#007AFF]/40 text-xs font-normal w-6">0{idx + 1}</span>
                  <span>{link.label}</span>
                </a>
              </li>
            ))}
          </ul>

          <div className="mt-14 pt-10 border-t border-white/5 space-y-5">
            <p className="overline text-zinc-500">Get in touch</p>
            <a
              href="mailto:contact@airstagex.com"
              className="flex items-center gap-3 text-zinc-300 hover:text-white text-sm transition-colors"
              data-testid="drawer-email"
            >
              <Mail className="w-4 h-4 text-[#007AFF]" />
              contact@airstagex.com
            </a>
            <div className="flex items-center gap-3 text-zinc-300 text-sm">
              <MapPin className="w-4 h-4 text-[#007AFF]" />
              London, United Kingdom
            </div>
            <div className="flex items-center gap-3 text-zinc-300 text-sm">
              <Phone className="w-4 h-4 text-[#007AFF]" />
              By appointment only
            </div>

            <a
              href="#access"
              onClick={(e) => handleNavClick(e, "#access")}
              className="btn-pill btn-pill-primary mt-8 w-full"
              data-testid="drawer-waitlist-btn"
            >
              Invitation Only
            </a>
          </div>
        </nav>
      </aside>
    </>
  );
}
